import { ensureRefusalProbe, getConsent } from '@bookkeeper/shared/consent';
import {
  haltReplayRecorder,
  installSnapshotNetworkGate,
  registerGateInstance,
  isDoNotTrack,
  maySendReplaySnapshot,
  createPreconsentMaskAttributeFn,
  createPreconsentMaskTextFn,
  omitKeys,
  PRECONSENT_CONSENT_STORAGE_NAME,
  parsePreconsentTelemetryMode,
  PRECONSENT_REPLAY_ONLY_EVENT,
  PRECONSENT_SESSION_RECORDING_BASE,
  shouldRecordPreconsent,
  snapshotMayLeave,
  type PreconsentConsentStatus,
  type PreconsentTelemetryMode,
} from '@bookkeeper/shared/preconsent-telemetry';
import posthog from 'posthog-js';
import type { CaptureResult, PostHog } from 'posthog-js';

type PostHogInitConfig = NonNullable<Parameters<typeof posthog.init>[1]>;

/**
 * Full pre-consent telemetry on www.bookeper.pl (BOO-5163). Owner flag, default OFF:
 *
 *   VITE_TELEMETRY_PRECONSENT_FULL = "true" | "pending" | "all"
 *   (the apps/frontend counterpart is NEXT_PUBLIC_TELEMETRY_PRECONSENT_FULL; same values)
 *
 * Same design as apps/frontend/src/lib/preconsent-telemetry.ts: a second PostHog instance
 * (`preconsent_replay`) with `persistence: 'memory'` (no cookie, no localStorage, no
 * sessionStorage), no person profile and only `$snapshot`, masked inputs, text and attribute values
 * (the landing is public marketing copy, so both are readable outside forms and `data-ph-mask` /
 * `.ph-no-capture` elements), no network headers or bodies, 100% sampling, requested at the first
 * pageview with sampling and triggers overridden. It starts when the project's replay config
 * answers: memory persistence keeps no copy, so a visit that ends before that response is not
 * recorded. Accept hands recording to the main instance, and a decline stops it in every mode ("all",
 * a former value that also recorded decliners, reads as "pending"; owner decision, #9200 Astra r1).
 * Do Not Track is honoured. With the flag off nothing here runs.
 */
export function getPreconsentTelemetryMode(
  value: string | undefined = import.meta.env.VITE_TELEMETRY_PRECONSENT_FULL
): PreconsentTelemetryMode {
  return parsePreconsentTelemetryMode(value);
}

const INSTANCE_NAME = 'preconsent_replay';

let instance: PostHog | undefined;
/**
 * A refusal halts the SDK instance for good (haltReplayRecorder), and PostHog serves a named instance again, so a
 * recording that starts after a refusal gets a NEW instance under a new name (#9200 Cursor).
 */
let generation = 0;
const instanceName = () => (generation === 0 ? INSTANCE_NAME : `${INSTANCE_NAME}_${generation}`);
/** True between a start and the next stop: the join key exists only while this instance records. */
let recording = false;

/** The pre-consent recording's session id right now (join key stamped on main-instance events). */
export function readPreconsentReplaySessionId(): string | undefined {
  if (!recording) return undefined;
  try {
    // Only once the SDK reports the recorder started, and while the visitor is still admitted (#9200 Sentry ydA0c).
    if (instance?.sessionRecordingStarted?.() !== true || isDoNotTrack()) return undefined;
    return instance.get_session_id() || undefined;
  } catch {
    return undefined;
  }
}

/** Instance config derived from the main init config: replay only, in memory, masked. */
export function buildPreconsentReplayConfig(
  main: PostHogInitConfig,
  getPathname: () => string = () => window.location.pathname
): PostHogInitConfig {
  const mainBeforeSend = Array.isArray(main.before_send) ? main.before_send[0] : main.before_send;
  // No `cookieless_mode` and no opt-out cookie settings: this instance has no consent state.
  const base = omitKeys(main, ['cookieless_mode', 'opt_out_capturing_persistence_type']);
  return {
    ...base,
    persistence: 'memory',
    // Its own consent key: the main instance's opt-out cookie (set for visitors who have not
    // chosen too) must not opt this one out.
    consent_persistence_name: PRECONSENT_CONSENT_STORAGE_NAME,
    opt_out_capturing_by_default: false,
    // No request waits in the SDK's queue after a refusal: a snapshot is sent when it is captured.
    request_batching: false,
    // Snapshots stay compressed (they are large); the network gate covers the async compression window.
    disable_compression: false,
    // The one transport whose dispatch the network gate holds for sure (installSnapshotNetworkGate).
    api_transport: 'XHR',
    cross_subdomain_cookie: false,
    person_profiles: 'never',
    respect_dnt: true,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    capture_heatmaps: false,
    capture_performance: { network_timing: true, web_vitals: false },
    disable_surveys: true,
    disable_session_recording: false,
    enable_recording_console_log: false,
    session_recording: {
      ...base.session_recording,
      ...PRECONSENT_SESSION_RECORDING_BASE,
      // The landing is marketing: public text stays readable outside forms and protected elements.
      maskTextFn: createPreconsentMaskTextFn(getPathname, () => true),
      maskAttributeFn: createPreconsentMaskAttributeFn(getPathname, () => true),
    },
    before_send: (event: CaptureResult | null): CaptureResult | null => {
      if (!event || event.event !== PRECONSENT_REPLAY_ONLY_EVENT) return null;
      if (stopped) return null;
      // Asked at send time: a decision made in another tab can reach a running recorder late.
      if (!maySendReplaySnapshot(getConsent()?.status, isDoNotTrack())) return null;
      return mainBeforeSend ? mainBeforeSend(event) : event;
    },
    loaded: (ph: PostHog) => {
      if (stopped) return;
      // Overrides sampling, the linked flag and the triggers, so the recording starts as soon as
      // the project's replay config answers (it cannot start before: posthog-js needs that
      // config, and memory persistence keeps no copy). The recorder script is fetched lazily.
      ph.startSessionRecording({
        sampling: true,
        linked_flag: true,
        url_trigger: true,
        event_trigger: true,
      });
    },
  };
}

function start(main: PostHogInitConfig): void {
  stopped = false;
  try {
    if (!instance) {
      const apiKey = import.meta.env.VITE_POSTHOG_KEY;
      if (!apiKey) return;
      // If this browser cannot remember a "no", nothing may record before a "yes" (#9200 Astra r8).
      if (!ensureRefusalProbe()) return;
      // The guarantee behind a refusal: every snapshot is checked against the current consent when it
      // is sent, whatever the SDK still holds (#9200 Astra r6).
      installSnapshotNetworkGate({
        apiHosts: [main.api_host],
        mayLeave: () => snapshotMayLeave(getConsent()?.status, isDoNotTrack()),
      });
      instance =
        posthog.init(apiKey, buildPreconsentReplayConfig(main), instanceName()) ?? undefined;
      registerGateInstance(instance); // the destinations the SDK resolved, not the config string
    } else {
      instance.startSessionRecording({
        sampling: true,
        linked_flag: true,
        url_trigger: true,
        event_trigger: true,
      });
    }
    recording = instance !== undefined;
  } catch {
    // Never let telemetry break the page.
  }
}

function stop(refused = false): void {
  stopped = true;
  recording = false;
  try {
    if (instance) haltReplayRecorder(instance, refused);
  } catch {
    // Never recorded.
  }
  if (refused && instance) {
    instance = undefined;
    generation += 1;
  }
}

/** Remembered at init so later consent changes can restart without rebuilding the main config. */
/**
 * Set by every stop, cleared by every start request. posthog-js runs `loaded` asynchronously, so a stop
 * that came first would otherwise be undone by it; while set, the recorder is not started and no
 * snapshot is sent.
 */
let stopped = false;

let mainConfig: PostHogInitConfig | undefined;

/**
 * Decide and apply for the current consent. Called right after the main init (first pageview), and
 * on every consent change. A no-op with the flag off.
 */
export function syncPreconsentReplay(
  consent: PreconsentConsentStatus,
  main: PostHogInitConfig | undefined = mainConfig,
  mode: PreconsentTelemetryMode = getPreconsentTelemetryMode()
): void {
  if (mode === 'off') return;
  if (main) mainConfig = main;
  if (shouldRecordPreconsent(mode, consent, window.location.pathname) && mainConfig) {
    start(mainConfig);
  } else {
    stop(consent === 'declined' || isDoNotTrack());
  }
}

export function resetPreconsentReplayForTests(): void {
  instance = undefined;
  generation = 0;
  recording = false;
  stopped = false;
  mainConfig = undefined;
}
